import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useAutoUpdate } from "@/lib/useAutoUpdate";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Nav from "@/components/Nav";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Practice from "@/pages/Practice";
import Dashboard from "@/pages/Dashboard";
import ContestDetail from "@/pages/ContestDetail";
import License from "@/pages/License";
import Admin from "@/pages/Admin";
import RaceControl from "@/pages/RaceControl";
import DebugIntelligence from "@/pages/DebugIntelligence";
import SopLibrary from "@/pages/SopLibrary";
import AdNetworkAdmin from "@/pages/AdNetworkAdmin";
import InterestOnboarding from "@/pages/InterestOnboarding";
import RateCard from "@/pages/RateCard";
import Calendar from "@/pages/Calendar";
import Flyer from "@/pages/Flyer";
import Profile from "@/pages/Profile";
import Lobby from "@/pages/Lobby";
import VerifyLicense from "@/pages/VerifyLicense";
import ForgotPassword from "@/pages/ForgotPassword";
import ClaimPrize from "@/pages/ClaimPrize";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEstate from "@/pages/VerifyEstate";
import RacingTeam from "@/pages/RacingTeam";
import KycComplete from "@/pages/KycComplete";
import SprintRace from "@/pages/SprintRace";
import BuyCredits from "@/pages/BuyCredits";
import PaymentReturn from "@/pages/PaymentReturn";
import ActivateBooklet from "@/pages/ActivateBooklet";
import Results from "@/pages/Results";
import EventEntry from "@/pages/EventEntry";
import MessagingPolicy from "@/pages/MessagingPolicy";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Publication from "@/pages/Publication";
import MobileGate from "@/components/MobileGate";
import Footer from "@/components/Footer";
import AdvAuth from "@/pages/adv/AdvAuth";
import AdvDashboard from "@/pages/adv/AdvDashboard";
import AdvCampaigns from "@/pages/adv/AdvCampaigns";
import AdvCreateCampaign from "@/pages/adv/AdvCreateCampaign";
import AdvCampaignDetail from "@/pages/adv/AdvCampaignDetail";
import AdvCreativeStudio from "@/pages/adv/AdvCreativeStudio";
import AdvAnalytics from "@/pages/adv/AdvAnalytics";
import AdvBilling from "@/pages/adv/AdvBilling";
import AdvAccount from "@/pages/adv/AdvAccount";
import AdvertiserReview from "@/pages/AdvertiserReview";
import { SettingsProvider } from "@/context/SettingsContext";

function Protected({ children, role }) {
  const { user } = useAuth();
  if (user === null) return <div className="p-10 text-race-sub">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "advertiser") return <Navigate to="/advertiser" replace />;
  if (role && user.role !== role) return <Navigate to="/dashboard" replace />;
  return children;
}

function AdvProtected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="p-10 text-race-sub">Loading…</div>;
  if (!user) return <Navigate to="/advertiser/login" replace />;
  if (user.role !== "advertiser") return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  return children;
}

function Shell() {
  const loc = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [loc.pathname, loc.search]);
  useAutoUpdate(loc.pathname);
  const adv = loc.pathname.startsWith("/advertiser") || loc.pathname === "/advertise";
  const bare = loc.pathname === "/" || loc.pathname === "/publication" || adv;
  return (
    <div className="App min-h-screen bg-race-bg">
      {!bare && <Nav />}
      {!adv && <MobileGate />}
      <div className={bare ? "" : "app-main max-w-6xl mx-auto px-4 py-8"}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Auth mode="login" />} />
          <Route path="/register" element={<Auth mode="register" />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/admin-login" element={<Auth mode="login" admin />} />
          <Route path="/verify/:prefix/:id" element={<VerifyEstate />} />
          <Route path="/practice" element={<Practice />} />
          <Route path="/flyer/:id" element={<Flyer />} />
          <Route path="/e/:id" element={<EventEntry />} />
          <Route path="/messaging-policy" element={<MessagingPolicy />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/publication" element={<Publication />} />
          <Route path="/claim/:token" element={<ClaimPrize />} />
          <Route path="/verify-license/:token" element={<VerifyLicense />} />
          {/* Advertiser portal (separate experience) */}
          <Route path="/advertise" element={<Navigate to="/advertiser/register" replace />} />
          <Route path="/advertiser/login" element={<AdvAuth mode="login" />} />
          <Route path="/advertiser/register" element={<AdvAuth mode="register" />} />
          <Route path="/advertiser" element={<AdvProtected><AdvDashboard /></AdvProtected>} />
          <Route path="/advertiser/campaigns" element={<AdvProtected><AdvCampaigns /></AdvProtected>} />
          <Route path="/advertiser/campaigns/new" element={<AdvProtected><AdvCreateCampaign /></AdvProtected>} />
          <Route path="/advertiser/campaigns/:id" element={<AdvProtected><AdvCampaignDetail /></AdvProtected>} />
          <Route path="/advertiser/campaigns/:id/edit" element={<AdvProtected><AdvCreateCampaign /></AdvProtected>} />
          <Route path="/advertiser/creative-studio" element={<AdvProtected><AdvCreativeStudio /></AdvProtected>} />
          <Route path="/advertiser/analytics" element={<AdvProtected><AdvAnalytics /></AdvProtected>} />
          <Route path="/advertiser/billing" element={<AdvProtected><AdvBilling /></AdvProtected>} />
          <Route path="/advertiser/account" element={<AdvProtected><AdvAccount /></AdvProtected>} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
          <Route path="/license" element={<Protected><License /></Protected>} />
          <Route path="/profile" element={<Protected><Profile /></Protected>} />
          <Route path="/racing-team" element={<Protected><RacingTeam /></Protected>} />
          <Route path="/kyc/complete" element={<Protected><KycComplete /></Protected>} />
          <Route path="/sprint-demo" element={<Protected><SprintRace /></Protected>} />
          <Route path="/buy-credits" element={<Protected><BuyCredits /></Protected>} />
          <Route path="/activate-booklet" element={<Protected><ActivateBooklet /></Protected>} />
          <Route path="/payment/success" element={<Protected><PaymentReturn /></Protected>} />
          <Route path="/payment/cancel" element={<Protected><PaymentReturn cancelled /></Protected>} />
          <Route path="/lobby/:id" element={<Protected><Lobby /></Protected>} />
          <Route path="/results/:id" element={<Protected><Results /></Protected>} />
          <Route path="/contests/:id" element={<Protected><ContestDetail /></Protected>} />
          <Route path="/admin" element={<Protected role="admin"><Admin /></Protected>} />
          <Route path="/admin/race-control" element={<Protected role="admin"><RaceControl /></Protected>} />
          <Route path="/admin/debug-intelligence" element={<Protected role="admin"><DebugIntelligence /></Protected>} />
          <Route path="/admin/sop-library" element={<Protected role="admin"><SopLibrary /></Protected>} />
          <Route path="/admin/ad-network" element={<Protected role="admin"><AdNetworkAdmin /></Protected>} />
          <Route path="/admin/advertisers" element={<Protected role="admin"><AdvertiserReview /></Protected>} />
          <Route path="/onboarding/interests" element={<Protected><InterestOnboarding /></Protected>} />
          <Route path="/rate-card" element={<Protected role="admin"><RateCard /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      {!adv && <Footer />}
    </div>
  );
}

function App() {
  useEffect(() => { document.documentElement.classList.add("dark"); }, []);
  return (
    <SettingsProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors />
          <Shell />
        </BrowserRouter>
      </AuthProvider>
    </SettingsProvider>
  );
}

export default App;
